import stopManager from "../../Lib/Utils/StopManager";
import { calculatePrice, convertISO8601ToDuration } from "../../Lib/Utils/validationUtil";
import { NormalizedBookedShuttle } from "../../Lib/Utils/shuttleStops";
import { IBookedOffer } from "../../Orchestrator/Types/Interface/IOffer";
import { ITrip } from "../../Orchestrator/Types/Interface/ITrip";
import { hhmmInDubai } from "../../Lib/Utils/shuttleTime";
import { TERMINAL_ADMISSION_STATUSES } from "./hasPendingExchange";

/**
 * Extracted from JourneyOptionsPanel when the end-to-end drawer was deleted:
 * the panel is gone but onward-travel hydration still needs this normalisation.
 */

/**
 * Normalize a booked shuttle offer using the covered trip from the booking.
 * Origin/destination refs, times and service number are read from the trip's
 * first/last legs; price from admissions; passenger count from admission ids.
 */
export const normalizeBookedShuttle = (
    offer: IBookedOffer,
    tripsById: Map<string, ITrip>,
    /** Booking passengers, used to map admission passengerIds -> externalRef. */
    bookingPassengers: { id: string; externalRef: string }[] = [],
    /**
     * Which admissions the riders/price/count are read from.
     *
     * "held" (the default, and every money path) counts the riders still being
     * carried. "terminal" is its exact mirror and exists for the CANCELLED
     * group on booking details: a refunded offer has no held admissions at all,
     * so the default reads it as an empty shuttle — count 0, no riders, price 0
     * — and there is nothing left to draw. A split party's offer answers both
     * ways, one rider each, which is what puts the mover's cancelled ticket
     * below and the stayer's live ticket above.
     */
    opts: { admissions?: "held" | "terminal" } = {}
): NormalizedBookedShuttle => {
    const trip = tripsById.get(offer.tripCoverage?.coveredTripId || "");
    const legs = trip?.legs || [];
    const firstLeg = legs[0];
    const lastLeg = legs[legs.length - 1];

    const originRef = trip?.origin?.stopPlaceRef || firstLeg?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "";
    const destinationRef = trip?.destination?.stopPlaceRef || lastLeg?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || "";
    const originName = stopManager.getStopNameById(originRef) || originRef;
    const destinationName = stopManager.getStopNameById(destinationRef) || destinationRef;

    const depDateTime = firstLeg?.timedLeg?.start?.serviceDeparture?.timetabledTime;
    const depTime = hhmmInDubai(depDateTime);
    const arrTime = hhmmInDubai(lastLeg?.timedLeg?.end?.serviceArrival?.timetabledTime);
    // vehicleNumbers FIRST, joined the same way GetShuttleE2EOffersAction builds
    // it ("SH001-03"). publishedServiceName is a route label ("Shuttle - X"), so
    // preferring it made the booked entry unmatchable against the offers list and
    // the owned card disagreed with the time picker.
    const vehicleNumbers = firstLeg?.timedLeg?.service?.vehicleNumbers;
    const trainNumber =
        (Array.isArray(vehicleNumbers) && vehicleNumbers.length > 0
            ? vehicleNumbers.join(", ")
            : firstLeg?.timedLeg?.service?.publishedServiceName) || "";
    const durationLabel = convertISO8601ToDuration(trip?.duration || "");

    // Riders still being carried. A shuttle offer holds one admission per rider
    // and a change-date refunds only the MOVING passenger's, so a split party's
    // offer survives with the mover REFUNDED beside the stayer's live ticket.
    // Counting every admission drew "2 shuttle tickets" and the two-passenger
    // price for the one ticket that was left.
    //
    // TERMINAL, not "not OWNED": PREBOOKED and ON_HOLD are unpaid but the rider
    // is still on the offer, and a checkout basket must price them.
    // No cast: `offer` is a typed IBookedOffer here, unlike the raw
    // `booking.bookedOffers` the sibling filters read. Casting would let a
    // rename of IAdmission.status compile clean and silently disable this.
    const wantTerminal = opts.admissions === "terminal";
    const admissions = (offer.admissions || []).filter(
        (a) => TERMINAL_ADMISSION_STATUSES.has(a?.status || "") === wantTerminal
    );
    const totalPrice = admissions.reduce((sum, a) => sum + calculatePrice(a?.price), 0);
    const passengerIds = new Set<string>();
    admissions.forEach((a) => (a?.passengerIds || []).forEach((id) => passengerIds.add(id)));
    // Admissions carry booking passenger IDs; every consumer (checklist, offers
    // request, selection payload) keys off externalRef. Unmapped ids are dropped —
    // sending an id where a ref is expected prices the wrong passenger.
    const passengerRefs = [...passengerIds]
        .map((id) => bookingPassengers.find((p) => p?.id === id)?.externalRef)
        .filter((ref): ref is string => !!ref);

    return {
        offerId: offer.offerId,
        // Derived per-side in mapBookedShuttlesToSides from originRef/destinationRef.
        stopId: "",
        originRef,
        destinationRef,
        originName,
        destinationName,
        // back-compat defaults; mapBookedShuttlesToSides resolves the per-side names.
        stopName: originName,
        stationName: destinationName,
        depTime,
        arrTime,
        depDateTime,
        trainNumber,
        durationLabel,
        totalPrice,
        passengerCount: passengerIds.size,
        passengerRefs,
    };
};
